import { useEffect, useState, type ReactNode } from 'react'
import { requestPersistence } from '../platform/persistence'
import { copyText } from '../platform/clipboard'
import { getDb } from '../storage/db'
import common from './common.module.css'
import { t } from './strings'

type State = { status: 'opening' } | { status: 'ready' } | { status: 'error'; error: unknown }

/**
 * Abre la base de datos antes de pintar nada que dependa de ella. Si el navegador no la deja abrir
 * (pestaña privada, falta de espacio, un fallo de Safari), lo dice con claridad: en el iPad no hay
 * inspector, así que un error mudo sería imposible de entender.
 */
export function StorageGate({ children }: { children: ReactNode }) {
  const [state, setState] = useState<State>({ status: 'opening' })
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    let cancelled = false
    getDb()
      .open()
      .then(
        () => {
          if (cancelled) return
          setState({ status: 'ready' })
          void requestPersistence()
        },
        (error: unknown) => {
          if (cancelled) return
          console.error('No se pudo abrir la base de datos', error)
          setState({ status: 'error', error })
        },
      )
    return () => {
      cancelled = true
    }
  }, [attempt])

  if (state.status === 'ready') return <>{children}</>
  if (state.status === 'opening') return <p className={common.muted}>{t.storage.opening}</p>

  const details =
    state.error instanceof Error
      ? `${state.error.name}: ${state.error.message}`
      : String(state.error)
  return (
    <div className={common.noticeError} role="alert">
      <h1>{t.storage.errorTitle}</h1>
      <p>{t.storage.errorText}</p>
      <pre className={common.mono}>{details}</pre>
      <div className={common.row}>
        <button
          className="primary"
          onClick={() => {
            setState({ status: 'opening' })
            setAttempt((value) => value + 1)
          }}
        >
          {t.storage.retry}
        </button>
        <button onClick={() => void copyText(details)}>{t.storage.copyDetails}</button>
      </div>
    </div>
  )
}
